import React, { useState, FormEvent, ChangeEvent } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ email?: boolean; password?: boolean }>({});
  const [alert, setAlert] = useState<{ type: string; message: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors({});
    setAlert(null);
    const newErrors: { email?: boolean; password?: boolean } = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = true;
    if (password.length < 8) newErrors.password = true;
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      onClose();
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setAlert({ type: 'error', message: 'Invalid email or password.' });
      } else {
        setAlert({ type: 'error', message: 'Something went wrong. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-overlay" onClick={onClose}>
      <div className="login-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="login-left">
          <div className="login-left-inner">
            <div className="login-tag"><span className="login-tag-dot"></span><span className="login-tag-line"></span>Global Expansion Platform</div>
            <h1 className="login-heading">Expand smarter.<br /><em>Decide with data.</em></h1>
            <p className="login-desc">Make confident market entry decisions backed by real data — 150+ markets, zero guesswork.</p>
            <div className="login-stats">
              <div className="login-stat"><div className="login-stat-n">150+</div><div className="login-stat-l">Markets simulated</div></div>
              <div className="login-stat"><div className="login-stat-n">72h</div><div className="login-stat-l">To simulation</div></div>
              <div className="login-stat"><div className="login-stat-n">100%</div><div className="login-stat-l">Free to use</div></div>
            </div>
          </div>
          <div>
            <div className="login-quote">
              <div className="login-quote-bar"></div>
              <div>
                <div className="login-quote-text">"Our European market entry simulation was ready in 72 hours. It completely changed how we approached our expansion strategy."</div>
                <div className="login-quote-by">CEO, Goodbye Kansas</div>
              </div>
            </div>
            <div className="login-avatars"><div className="login-avatars-text"><strong>240+ executives</strong> trust our platform</div></div>
          </div>
        </div>
        <div className="login-right">
          <div className="login-top-bar"></div>
          <div className="login-right-inner">
            <div className="login-eyebrow">Sign In</div>
            <div className="login-title">Welcome back</div>
            <div className="login-sub">Access your simulations — or <a href="#" style={{ color: 'var(--sage)', fontWeight: 600, textDecoration: 'none' }}>create an account</a> to get started today.</div>
            {alert && (
              <div className={`login-alert ${alert.type}`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                <span>{alert.message}</span>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="login-group">
                <label>Email address</label>
                <input type="email" value={email} onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} placeholder="you@company.com" className={errors.email ? 'err' : ''} />
                {errors.email && <div className="login-field-err">Enter a valid email address</div>}
              </div>
              <div className="login-group">
                <label>Password</label>
                <div className="login-pw-wrap">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)} placeholder="Your password" className={errors.password ? 'err' : ''} />
                  <button type="button" className="login-pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      {showPassword
                        ? <><path d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>
                        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                      }
                    </svg>
                  </button>
                </div>
                {errors.password && <div className="login-field-err">Password must be at least 8 characters</div>}
              </div>
              <div className="login-meta">
                <label className="login-remember"><input type="checkbox" /><span>Remember me</span></label>
                <a href="#" className="login-forgot">Forgot password?</a>
              </div>
              <button type="submit" className={`login-submit ${loading ? 'loading' : ''}`} disabled={loading}>
                {loading
                  ? <div className="login-spinner"></div>
                  : <><span>Sign in to your account</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg></>
                }
              </button>
            </form>
            <div className="login-or"><div className="login-or-line"></div><span>or continue with</span><div className="login-or-line"></div></div>
            <button className="login-google">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>
            <div className="login-foot">
              <p>No account yet? <a href="#">Sign up →</a></p>
              <div className="login-secure">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                256-bit SSL<span className="login-secure-dot"></span>SOC 2 Type II<span className="login-secure-dot"></span>GDPR
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
